import {Injectable} from '@angular/core';
import {ActivatedRoute, ParamMap, Params, Router} from '@angular/router';
import {BehaviorSubject, distinct, filter, map, Observable, tap} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AsaxBookmarkingService {
  private queryParamMap = new Map<string, string>();
  private readonly queryParamsSubject = new BehaviorSubject<Params>({});

  constructor(private readonly router: Router,
              private readonly activatedRoute: ActivatedRoute) {
    this.initializeQueryParamsMap();

    this.activatedRoute.queryParams.pipe(
      tap(queryParams => this.queryParamsSubject.next(queryParams))
    ).subscribe();
  }

  getQueryParam(queryParam: string): Observable<string> {
    return this.activatedRoute.queryParamMap.pipe(
      filter((queryParamMap: ParamMap) => queryParamMap.has(queryParam)),
      map((queryParamMap: ParamMap) => queryParamMap.get(queryParam)),
      distinct()
    );
  }

  getQueryParams(): Observable<Params> {
    return this.queryParamsSubject.asObservable();
  }

  addParamsToUrl(params: Params): void {
    Object.entries(params).forEach(([queryParam, value]: [string, string]) => this.queryParamMap.set(queryParam, value));
    this.updateUrlWithQueryParamsMap();
  }

  removeParamsFromUrl(...params: string[]): void {
    params?.forEach((param: string) => this.queryParamMap.delete(param));
    this.updateUrlWithQueryParamsMap();
  }

  removeNotAllowedParams(params: string[]): void {
    this.queryParamMap = new Map([...this.queryParamMap].filter(([key, _]) => params.includes(key)));

    this.updateUrlWithQueryParamsMap();
  }

  private updateUrlWithQueryParamsMap(): void {
    const queryParams = Object.fromEntries(this.queryParamMap);
    this.router.navigate(['.'], {relativeTo: this.activatedRoute, queryParams});
  }

  private initializeQueryParamsMap(): any {
    const activatedQueryParams = getActivatedQueryParams();

    Object.entries(activatedQueryParams)
      .forEach(([queryParam, value]: [string, string]) => this.queryParamMap.set(queryParam, value));
  }

  public getQueryParamsFromPath(path: string): string[] {
    return decodeURI(path)
            .replace('?', '')
            .split('&')
            .map(param => param.split('=')[0]);
  }
}

const getActivatedQueryParams = () => {
  return decodeURI(window.location.search)
    .replace('?', '')
    .split('&')
    .map(param => param.split('='))
    .reduce((values, [key, value]) => {
      values[key] = value
      return values
    }, {});
}
